import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Chip,
  DataTable,
  Field,
  Input,
  Pager,
  Segmented,
  Select,
  StateBlock,
  type Column,
} from '@w7/ui'
import { count, EMPTY, lll } from '@/lib/format'
import { CmPanel } from '@/features/catalog/CmPanel'
import { MemberPicker } from '@/features/reports/MemberPicker'
import type { InvalidBetRow, InvalidBetsSport, InvalidBetsTab } from './invalidBetsTypes'
import { INVALID_BETS_SPORTS } from './invalidBetsTypes'
import { useInvalidBets } from './useInvalidBets'
import './invalid-bets.css'

/**
 * Invalid Bets — Lab All Logs `/var-invalid-betlogs`.
 * Filters + VARBet / Invalid Bets tabs match Winner; live via CM-API.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

const TAB_ITEMS: { id: InvalidBetsTab; label: string }[] = [
  { id: 'var-bet', label: 'VARBet' },
  { id: 'invalid-bets', label: 'Invalid Bets' },
]

function todayInputValue(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function sideTone(type: string): 'back' | 'lay' | undefined {
  const t = type.trim().toUpperCase()
  if (t === 'BACK') return 'back'
  if (t === 'LAY') return 'lay'
  return undefined
}

export function InvalidBetsPage() {
  const [page, setPage] = useState(1)
  const [tab, setTab] = useState<InvalidBetsTab>('var-bet')
  const [sport, setSport] = useState<InvalidBetsSport>('All')
  const [members, setMembers] = useState<string[]>([])
  const [fromDate, setFromDate] = useState(() => todayInputValue())
  const [toDate, setToDate] = useState(() => todayInputValue())
  const [applied, setApplied] = useState({
    tab: 'var-bet' as InvalidBetsTab,
    sport: 'All' as InvalidBetsSport,
    members: [] as string[],
    fromDate: todayInputValue(),
    toDate: todayInputValue(),
  })

  const { data, isLoading, isError, error, refetch, isFetching } = useInvalidBets({
    page,
    pageSize: PAGE_SIZE,
    tab: applied.tab,
    sport: applied.sport,
    members: applied.members,
    fromDate: applied.fromDate,
    toDate: applied.toDate,
  })

  const memberRange = useMemo(
    () => ({ from: fromDate, to: toDate }),
    [fromDate, toDate],
  )

  const columns = useMemo<Column<InvalidBetRow>[]>(() => {
    const indexCol: Column<InvalidBetRow> = {
      id: 'index',
      header: '#',
      align: 'right',
      numeric: true,
      cell: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
    }

    const typeCol: Column<InvalidBetRow> = {
      id: 'type',
      header: 'Type',
      cell: (row) => {
        const tone = sideTone(row.type)
        if (!tone) return row.type || EMPTY
        return (
          <Chip size="sm" tone={tone}>
            {row.type}
          </Chip>
        )
      },
    }

    if (applied.tab === 'var-bet') {
      return [
        indexCol,
        { id: 'player', header: 'Player', cell: (row) => row.playerName || EMPTY },
        { id: 'event', header: 'Event', cell: (row) => row.event || EMPTY },
        {
          id: 'placedAt',
          header: 'Place Date',
          cell: (row) => (row.placedAt ? lll(row.placedAt) : EMPTY),
        },
        {
          id: 'voidAt',
          header: 'Void Date',
          cell: (row) => (row.updatedAt ? lll(row.updatedAt) : EMPTY),
        },
        typeCol,
        {
          id: 'selection',
          header: 'Horse Name',
          cell: (row) => row.selectionName || EMPTY,
        },
        {
          id: 'odds',
          header: 'Odds',
          align: 'right',
          numeric: true,
          cell: (row) => row.odds,
        },
        {
          id: 'stake',
          header: 'Stake',
          align: 'right',
          numeric: true,
          cell: (row) => row.stake,
        },
        { id: 'placeIp', header: 'Bet Place IP', cell: (row) => row.placeIp || EMPTY },
      ]
    }

    return [
      indexCol,
      { id: 'player', header: 'Player', cell: (row) => row.playerName || EMPTY },
      {
        id: 'placedAt',
        header: 'Place Date',
        cell: (row) => (row.placedAt ? lll(row.placedAt) : EMPTY),
      },
      {
        id: 'invalidAt',
        header: 'Invalid Date',
        cell: (row) => (row.updatedAt ? lll(row.updatedAt) : EMPTY),
      },
      typeCol,
      {
        id: 'selection',
        header: 'Horse Name',
        cell: (row) => row.selectionName || EMPTY,
      },
      {
        id: 'odds',
        header: 'Odds',
        align: 'right',
        numeric: true,
        cell: (row) => row.odds,
      },
      {
        id: 'stake',
        header: 'Stake',
        align: 'right',
        numeric: true,
        cell: (row) => row.stake,
      },
      { id: 'invalidBy', header: 'Invalid By', cell: (row) => row.invalidBy || EMPTY },
      { id: 'placeIp', header: 'Bet Place IP', cell: (row) => row.placeIp || EMPTY },
      { id: 'invalidIp', header: 'Invalid IP', cell: (row) => row.invalidIp || EMPTY },
      {
        id: 'action',
        header: 'Action',
        cell: () => (
          <Button variant="secondary" size="sm" disabled>
            View
          </Button>
        ),
      },
    ]
  }, [applied.tab, page])

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  const getLogs = () => {
    setPage(1)
    setApplied({
      tab,
      sport,
      members: [...members],
      fromDate,
      toDate,
    })
  }

  const clearFilters = () => {
    const today = todayInputValue()
    setTab('var-bet')
    setSport('All')
    setMembers([])
    setFromDate(today)
    setToDate(today)
    setPage(1)
    setApplied({
      tab: 'var-bet',
      sport: 'All',
      members: [],
      fromDate: today,
      toDate: today,
    })
  }

  const onTabChange = (next: InvalidBetsTab) => {
    setTab(next)
    setPage(1)
    setApplied((prev) => ({ ...prev, tab: next }))
  }

  return (
    <CmPanel
      wide
      title="Invalid Bets"
      subtitle="VAR and invalid bet logs — Lab All Logs /var-invalid-betlogs"
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={isFetching}
          onClick={() => void refetch()}
        >
          Refresh
        </Button>
      }
    >
      <section className="cm-ib-filters" aria-label="Invalid bet filters">
        <div className="cm-ib-filters-row">
          <div className="cm-ib-filters-member">
            <MemberPicker
              selected={members}
              onChange={setMembers}
              source="hierarchy"
              range={memberRange}
              rootId={null}
              label="Member"
              allLabel="All members"
            />
          </div>
          <div className="cm-ib-filters-sport">
            <Field label="Sport">
              {({ id }) => (
                <Select
                  id={id}
                  value={sport}
                  onChange={(event) =>
                    setSport(event.target.value as InvalidBetsSport)
                  }
                  options={INVALID_BETS_SPORTS.map((s) => ({
                    value: s,
                    label: s,
                  }))}
                />
              )}
            </Field>
          </div>
          <div className="cm-ib-filters-date">
            <Field label="From Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="cm-ib-filters-date">
            <Field label="To Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="cm-ib-filters-tab">
            <Segmented
              label="Log type"
              items={TAB_ITEMS}
              value={tab}
              onChange={onTabChange}
            />
          </div>
          <div className="cm-ib-filters-actions">
            <Button variant="primary" onClick={getLogs}>
              Get Logs
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      </section>

      {isError ? (
        <div className="cm-hint" role="alert">
          <p>{error instanceof Error ? error.message : 'Failed to load logs'}</p>
          <Button type="button" variant="secondary" busy={isFetching} onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : null}

      <section className="cm-ib-table" aria-label={applied.tab === 'var-bet' ? 'VAR bets' : 'Invalid bets'}>
        <header className="cm-ib-table-head">
          <h2 className="cm-ib-table-title">
            {applied.tab === 'var-bet' ? 'VAR bets' : 'Invalid bets'}
          </h2>
          <p className="cm-hint">{isLoading ? 'Loading…' : `${count(total)} in this list`}</p>
        </header>
        <DataTable
          label={applied.tab === 'var-bet' ? 'VAR bet logs' : 'Invalid bet logs'}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={8}
          empty={
            <StateBlock icon="sliders" title="No data found">
              {applied.tab === 'var-bet'
                ? 'No VAR bet rows for these filters.'
                : 'No invalid bet rows for these filters.'}
            </StateBlock>
          }
        />
        <Pager
          page={data?.page ?? page}
          pageCount={pageCount}
          onChange={setPage}
          total={total}
          pageSize={PAGE_SIZE}
        />
      </section>
    </CmPanel>
  )
}
